import * as Actions from '../actions/firmActions';
import Firm from '../../entities/firm';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type FirmAction = { 
    type: string; 
    firm: Firm | null; 
    firmId: string | Firm; 
    partial: boolean; 
    lastFetch: number; 
    firms: Firm[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Firm> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Firm[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Firm> | null, firmId: string, userId: string, updates?: Firm[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'firm', 'firms', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Firm>} data
 * @param {Firm[]} [updates]
 * @returns {ArrayMap<Firm>}
 */
function updateStorage(data: ArrayMap<Firm>, updates?: Firm[]): ArrayMap<Firm> {
	if (logging) console.debug('⮂ firmReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Firm[]} [updates]
 * @returns {ArrayMap<Firm>}
 */
function updateStorageNoReturn(updates?: Firm[]): void {
	if (logging) console.debug('⮂ firmReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Firm[]} [data]
 * @returns {ArrayMap<Firm>}
 */
function createState(data?: Firm[] | ArrayMap<Firm>): ArrayMap<Firm> {
	if (!data) return ArrayMapRegistry.clone<Firm>('firm', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Firm>('firm', data.map(e => e));
    else return ArrayMapRegistry.clone<Firm>('firm', data);
}

function firms(state: ArrayMap<Firm>, action: FirmAction): ArrayMap<Firm> {
	if (!state) state = createState();

	let st: ArrayMap<Firm>;

	switch (action.type) {
		// action.firm - the new value of firm with id action.id
		// action.firmId - id of the target, used if new value is null
		case Actions.FIRM_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ firmReducer: FETCH_ONE_SUCCESS', action);

			if (action.firm === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.firmId)) {
				st = state.clone();
				st.set(action.firm);
				// state.clear();

				updateStorageNoReturn([action.firm]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.firms
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.firms
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.firms
			 !partial, !stateEmpty, fetchEmpty	-> D return firms in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge firms in state newer than those in action.firms
		 */
		case Actions.FIRM_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.firms.length === 0;
			const fetched = action.firms.map(firm => new Firm({...firm, persisted: true}));

			if (logging) console.debug(`⮂ firmReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} firms with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ firmReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ firmReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Firm> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Firm[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(firm => {
					const current = st.get(firm.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && firm.status === 'D') {
							// Remove it from state
							toUpdate.push(firm);
							st.delete(firm);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (firm.addedOn > current.addedOn) {
								toUpdate.push(firm);
								st.set(firm);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (firm.status !== 'D') {
							toUpdate.push(firm);
							st.set(firm);
						}
						else if (logging) console.debug('⮂ firmReducer: partialMerge: skipping deleted firm not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ firmReducer: empty: firms', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ firmReducer: fullMerge: firms', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localFirm => {
                    // If this firm has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localFirm.persisted) {
                        const fetchedFirm = st.get(localFirm.id);
                        if (!fetchedFirm || localFirm.addedOn > fetchedFirm.addedOn) st.set(localFirm);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.FIRM_FETCH_ONE_FAILURE:
			console.error('FIRM_FETCH_ONE_FAILURE', action.firmId, action.err);

			// No update to storage required
			return state;

		case Actions.FIRM_FETCH_FAILURE:
			console.error('FIRM_FETCH_FAILURE', action.firmId, action.err);

			// No update to storage required
			return state;

		case Actions.FIRM_RESTORE: {
		    let firm = new Firm({...action.firm});
            st = state.clone();

            const existing = st.get(firm.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (firm.addedOn >= existing.addedOn) {
                    st.set(firm);
                    return updateStorage(st, [firm]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (firm.status === 'D') return state;

                st.set(firm);
                return updateStorage(st, [firm]);
            }
        }
            
		case Actions.FIRM_SAVE: {
		    // console.debug('⮂ FIRM_SAVE reducer', action.firm?.id);
		    
			// clone the firm
			let firm = new Firm({...action.firm});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new firm in the state
			// If this is a create, add to the queue
			const _firm = st.get(firm.id);

			if (!_firm) st.set(firm);
			else {
				if (firm.addedOn >= _firm.addedOn) st.set(firm);
			}

			return updateStorage(st, [firm]);
		}

		case Actions.FIRM_SAVE_MANY: {
		    if (logging) console.debug('⮂ FIRM_SAVE_MANY reducer', action.firms.map(firm => firm.id).join(','));
		    
			// clone the firms
			let toProcess = action.firms.map(firm => new Firm(firm));

			st = state.clone();
			// state.clear();

			const updates: Firm[] = [];

			toProcess.forEach(firm => {
				// Add or update the new firm in the state
				// If this is a create, add to the queue
				const existing = st.get(firm.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (firm.status !== 'D') {
						st.push(firm);
						updates.push(firm);
					}
					else if (logging) console.debug('⮂ FIRM_SAVE_MANY: skipping deleted firm not in state');
				}
				else {
					if (firm.addedOn >= existing.addedOn) {
						st.set(firm);
						updates.push(firm);
					}
					else if (logging) console.debug('⮂ Skipping, existing firm had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.FIRM_REMOVE: {
			// clone the firm
			const firm = new Firm({...action.firm});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(firm);

			return updateStorage(st, [firm]);
		}

		case Actions.FIRM_RESTORE_FAILURE:
		case Actions.FIRM_SAVE_FAILURE: {
		    console.warn('FIRM_SAVE_FAILURE reducer', action.firm?.id);
		    if (!action.firm?.id) {
				console.warn('FIRM_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const firm = ensure(Firm, action.firm);
			if (!firm) {
				console.error('Could not find firm after call to save');
				return state;
			}

			st = state.clone();

			const _firmRef = state.get(firm.id);

			if (_firmRef && (_firmRef.addedOn === firm.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _firm = new Firm({..._firmRef});
				_firm.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_firm.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('firm', _firm).then(r => {
						if (r.isErr()) console.error(`⮂ firmReducer: failed to save firm with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ firmReducer: saved firm with fatalError info');
					}).catch(err => console.error(`⮂ firmReducer: failed to save firm with fatalError info: ${err.message}`));

				st.set(_firm);
			}

			return updateStorage(st);
		}

		case Actions.FIRM_SAVE_MANY_FAILURE: {
		    console.warn('FIRM_SAVE_MANY_FAILURE reducer', action.firms.map(firm => firm.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.FIRM_RESTORE_SUCCESS:
		case Actions.FIRM_SAVE_SUCCESS: {
			if (!action.firm?.id) {
				console.warn('FIRM_SAVE_SUCCESS reducer');
                return state;
			}

			const firmRef = state.get(action.firm?.id);
			if (!firmRef) {
				console.warn('Could not find firm after call to save');
                return state;
			}

			st = state.clone();

			if (action.firm.addedOn >= firmRef.addedOn) {
				st.set(action.firm);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const firm = new Firm({...firmRef, persisted: true});
				st.set(firm);
			}

			return updateStorage(st, [action.firm]);
		}

		case Actions.FIRM_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ FIRM_SAVE_MANY_SUCCESS reducer', action.firms.map(firm => firm.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const firms: Firm[] = [];

			action.firms.forEach(e => {
				const firmRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!firmRef) console.warn('Could not find firm after call to save');
				else if (e.addedOn >= firmRef.addedOn) {
				    st.set(e);
				    firms.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const firm = new Firm({...firmRef, persisted: true});
					st.set(firm);
					firms.push(firm);
				}
			});

			return updateStorage(st, firms);
		}

		case Actions.FIRM_REMOVE_FAILURE: {
		    console.warn('FIRM_REMOVE_FAILURE action', action.firm?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.FIRM_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.FIRM_RESTORE_MANY: {
			if (logging) console.debug('⮂ FIRM_RESTORE_MANY reducer', action.firms.map(firm => firm.id).join(','));

			const toProcess = action.firms.map(firm => new Firm({...firm}));
			st = state.clone();

			const updates: Firm[] = [];

			toProcess.forEach(firm => {
				const existing = st.get(firm.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (firm.addedOn >= existing.addedOn) {
						st.set(firm);
						updates.push(firm);
					}
					else if (logging) console.debug('⮂ FIRM_RESTORE_MANY: skipping older version of firm');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (firm.status !== 'D') {
						st.set(firm);
						updates.push(firm);
					}
					else if (logging) console.debug('⮂ FIRM_RESTORE_MANY: skipping deleted firm not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.FIRM_REMOVE_MANY: {
			if (logging) console.debug('⮂ FIRM_REMOVE_MANY reducer', action.firms.map(firm => firm.id).join(','));

			const toProcess = action.firms.map(firm => new Firm({...firm}));
			st = state.clone();

			toProcess.forEach(firm => {
				st.delete(firm);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default firms;